import {
  Box,
  CardContent,
  CardHeader,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Form, FormikProvider, useFormik } from "formik";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import FieldErrorMessage from "../../components/FieldErrorMessage";
import SaveButton from "../../components/SaveButton";
import CardLayout from "../../layouts/CardLayout";
import errorHandler from "../../utils/errorHandler";
import { toast } from "react-toastify";
import EmailTemplateList from "../../services/emailTemplateLits";
import EditorJodit from "../../components/editor/editor";
import { NOSPACE_REGEX } from "../../utils/constants";
import { MESSAGE } from "../../utils/validationMessage";
import * as Yup from "yup";

function EditAddEmail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const editorContentValidation = (content, context) => {
    if (content === undefined || content === null) {
      return context.createError({ message: "Body is required" });
    }

    const plainText = content
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .trim();

    if (!plainText) {
      return context.createError({ message: "Body is required" });
    }

    if (/^\s|^(&nbsp;)+/i.test(content.replace(/<[^>]*>/g, ""))) {
      return context.createError({
        message: "Body should not start with a space",
      });
    }

    if (plainText.length < 10) {
      return context.createError({
        message: "Body must be at least 10 characters long",
      });
    }

    return true;
  };

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .required("Title is required")
      .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
      .min(2, "Please enter at least 2 characters")
      .max(100, "Title must not be more than 100 characters."),
    content: Yup.string().test(
      "custom-description-validation",
      "Body is required",
      function (value) {
        return editorContentValidation(value, this);
      }
    ),
    subject: Yup.string()
      .required("Subject is required")
      .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
      .min(2, "Please enter at least 2 characters")
      .max(100, "Subject must not be more than 100 characters."),
    slug: Yup.string()
      .required("Slug is required")
      .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE),
    status: Yup.string().required("Status is required"),
  });

  const formik = useFormik({
    initialValues: {
      slug: "",
      title: "",
      subject: "",
      status: "1", // default to "Active"
      content: "",
      id: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const res = id
          ? await EmailTemplateList.edit(values)
          : await EmailTemplateList.add(values);
        toast.success(res?.data?.message);
        navigate("/email-template");
      } catch (error) {
        errorHandler(error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    resetForm,
  } = formik;

  useEffect(() => {
    if (id) {
      const getTemplateById = async () => {
        try {
          const response = await EmailTemplateList.getById({ id });
          const resData = response?.data?.data;

          resetForm({
            values: {
              id: resData?.id,
              title: resData?.title || "",
              subject: resData?.subject || "",
              status: resData?.status?.toString() || "0",
              slug: resData?.slug || "",
              content: resData?.content || "",
            },
          });
        } catch (error) {
          errorHandler(error);
        }
      };

      getTemplateById();
    }
  }, [id, resetForm]);

  return (
    <Box>
      <Helmet>
        <title>{id ? "Edit" : "Add"} Email Template</title>
      </Helmet>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Email Template`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    name="title"
                    error={Boolean(touched.title && errors.title)}
                    {...getFieldProps("title")}
                  />
                  <FieldErrorMessage formik={formik} name="title" />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject"
                    name="subject"
                    error={Boolean(touched.subject && errors.subject)}
                    {...getFieldProps("subject")}
                  />
                  <FieldErrorMessage formik={formik} name="subject" />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Slug"
                    name="slug"
                    error={Boolean(touched.slug && errors.slug)}
                    {...getFieldProps("slug")}
                  />
                  <FieldErrorMessage formik={formik} name="slug" />
                </Grid>

                <Grid item xs={12}>
                  <label>Description</label>
                  <EditorJodit
                    formik={formik}
                    fieldName="content"
                    onChange={(val) => setFieldValue("content", val)}
                    data={values.content}
                  />
                  {touched.content && errors.content && (
                    <FormHelperText error>{errors.content}</FormHelperText>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel
                      id="status-select-label"
                      error={Boolean(touched.status && errors.status)}
                    >
                      Status
                    </InputLabel>
                    <Select
                      labelId="status-select-label"
                      id="status-select"
                      value={values.status}
                      label="Status"
                      onChange={(e) => setFieldValue("status", e.target.value)}
                      error={Boolean(touched.status && errors.status)}
                    >
                      <MenuItem value="1">Active</MenuItem>
                      <MenuItem value="0">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                  <FieldErrorMessage formik={formik} name="status" />
                </Grid>
              </Grid>
              <SaveButton loading={formik.isSubmitting} id={id} />
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default EditAddEmail;
